import {
  Box,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Text,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { AccountSetting } from '@/services/auth/account-settings';
import { AccountSettingGuard } from './account-setting-guard';

/**
 * Empty state shown when AI is not enabled.
 * Shows a settings link if the user has permission to edit workspace settings.
 */
export function AiNotEnabledFallback() {
  const { hasPermission } = usePermissions();
  const canEditSettings = hasPermission('account/edit') === true;

  return (
    <Box display="flex" alignItems="center" justifyContent="center" h="full" w="full">
      <EmptyState>
        <EmptyStateTitle>AI is not enabled</EmptyStateTitle>
        <EmptyStateDescription>AI features are not enabled for this workspace.</EmptyStateDescription>
        <EmptyStateActions>
          {canEditSettings ? (
            <Button variant="primary" href="/settings/ai">
              Go to AI settings
            </Button>
          ) : (
            <Text size="s" color="subtle">
              Ask your account admin to enable AI in settings.
            </Text>
          )}
        </EmptyStateActions>
      </EmptyState>
    </Box>
  );
}

/**
 * Renders children only when Customer.io AI is enabled for the account
 * (`account.public_settings.ai_enabled`). Renders nothing by default when AI is
 * disabled — pass a fallback to override. Thin wrapper over AccountSettingGuard.
 */

type AiEnabledGuardProps = { fallback?: React.ComponentType; children: React.ReactNode };

export function AiEnabledGuard({ fallback, children }: AiEnabledGuardProps) {
  return (
    <AccountSettingGuard setting={AccountSetting.AiEnabled} fallback={fallback}>
      {children}
    </AccountSettingGuard>
  );
}
